import React from 'react';

interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  companyName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
  title?: string;
  itemLabel?: string;
  /** Error message to display when deletion fails (keeps dialog open). */
  errorMessage?: string;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  isOpen,
  companyName,
  onConfirm,
  onCancel,
  isDeleting = false,
  title,
  itemLabel,
  errorMessage,
}) => {
  if (!isOpen) return null;

  const dialogTitle = title ?? 'Delete Company';
  const dialogItemLabel = itemLabel ?? 'delete';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[70] transition-opacity"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="fixed inset-0 flex items-center justify-center z-[70] p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            {/* Icon */}
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full" style={{ backgroundColor: '#FEE2E2' }}>
              <svg className="w-6 h-6" style={{ color: '#DC2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-lg font-bold text-center mb-2" style={{ color: '#1F2937' }}>
              {dialogTitle}
            </h3>

            {/* Message */}
            <p className="text-sm text-center mb-4" style={{ color: '#6B7280' }}>
              Are you sure you want to {dialogItemLabel} <span className="font-semibold">{companyName}</span>? This action cannot be undone.
            </p>

            {/* Error message */}
            {errorMessage && (
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm text-center" style={{ color: '#DC2626' }}>{errorMessage}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={onCancel}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium transition-all hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#DC2626' }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
